import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FileUploader from "src/components/file-upload";
import { AttachmentType } from "src/constants";
import {
  IProjectAttachment,
  IProjectOtherAssetsData,
  IProjectRouteParams,
} from "src/pages/projects/project/interfaces";
import { checkFilesCurrentlyUploading } from "src/components/file-upload/helpers";
import { getIsMobile } from "@songtradr/spa-common";
import { cloneDeep } from "lodash";
import { useParams } from "react-router-dom";
import useAuth from "src/auth/use-auth";
import { IDeleteSelectedFile } from "src/components/file-upload/interfaces";
import MobileDrawer from "src/components/mobile-drawer";
import { Typography } from "antd";
import styles from "src/pages/projects/project/styles";
import NameLinks, { INameLinkConfig } from "../name-links";
import {
  DocumentsTableData,
  DocumentsTableColumns,
} from "../../../documents/table-data";
import { IProjectUploadFile } from "../../../details/components/interfaces";
import {
  FileDrawerTypes,
  generateDrawer,
} from "../../../documents/components/mobile-drawers";

interface IProps {
  projectOtherAssets: IProjectOtherAssetsData;
  musicAssetsAttachments: IProjectAttachment[];
  updateProjectOtherAssets: (
    projectOtherAssets: IProjectOtherAssetsData
  ) => void;
}

const MusicAssets = ({
  projectOtherAssets,
  musicAssetsAttachments,
  updateProjectOtherAssets,
}: IProps): ReactElement => {
  const { t } = useTranslation();
  const isMobile = getIsMobile();
  const { getAccessToken, organisationId } = useAuth();
  const [
    musicAssetsAttachmentsFileList,
    setMusicAssetsAttachmentsFileList,
  ] = useState<IProjectUploadFile[]>([]);

  const handleSetMusicAssetsAttachmentsFileList = useCallback(
    (list: IProjectUploadFile[]) => {
      setMusicAssetsAttachmentsFileList(list);
    },
    []
  );
  const [accessToken, setAccessToken] = useState("");
  const { id: projectId } = useParams<IProjectRouteParams>();
  const [
    selectedFileToDelete,
    setSelectedFileToDelete,
  ] = useState<IDeleteSelectedFile>();

  const handleSetSelectedFileToDelete = useCallback(
    (file: IDeleteSelectedFile | undefined) => {
      setSelectedFileToDelete(file);
    },
    []
  );
  const [showFileUploadDeleteModal, setShowFileUploadDeleteModal] = useState(
    false
  );
  const handleShowFileUploadDeleteModal = useCallback((show: boolean) => {
    setShowFileUploadDeleteModal(show);
  }, []);

  const [isDrawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [activeFile, setActiveFile] = useState<IProjectUploadFile>();
  const handleSetActiveFile = useCallback(
    (file: IProjectUploadFile | undefined) => {
      setActiveFile(file);
    },
    []
  );
  const [activeDrawer, setActiveDrawer] = useState<FileDrawerTypes>(
    FileDrawerTypes.FileInfoDrawer
  );
  const handleSetActiveDrawer = useCallback((drawerType: FileDrawerTypes) => {
    setActiveDrawer(drawerType);
  }, []);

  const toggleDrawer = useCallback(() => {
    setDrawerVisible((state) => !state);
  }, []);

  useEffect(() => {
    if (!checkFilesCurrentlyUploading(musicAssetsAttachmentsFileList)) {
      setMusicAssetsAttachmentsFileList(cloneDeep(musicAssetsAttachments));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void (() => {
      const userAccessToken: string = getAccessToken();
      setAccessToken(userAccessToken);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fileUploader = (
    <FileUploader
      attachmentType={AttachmentType.MusicAsset}
      invalidFileSizeErrorMessage={t("File size exceeds maximum limit of 2GB")}
      existingFiles={musicAssetsAttachmentsFileList}
      setFileList={handleSetMusicAssetsAttachmentsFileList}
      tableData={DocumentsTableData({
        filesList: musicAssetsAttachmentsFileList,
        accessToken,
        projectId,
        organisationId,
        setShowModal: handleShowFileUploadDeleteModal,
        setSelectedFileToDelete: handleSetSelectedFileToDelete,
        toggleDrawer,
        setActiveDrawer: handleSetActiveDrawer,
        setActiveFile: handleSetActiveFile,
      })}
      selectedFileToDelete={selectedFileToDelete}
      tableColumns={DocumentsTableColumns(isMobile, t)}
      showModal={showFileUploadDeleteModal}
      setShowModal={handleShowFileUploadDeleteModal}
    />
  );

  const config: INameLinkConfig = {
    fieldName: "musicassets",
    nameLink: "musicAssets",
    nameLinkVersion: "musicAssetsVersion",
    testDataId: "music-assets",
    nameHelpTextTranslationKey: "ProjectsPage##musicSection##Music Assets Name",
    sectionHeaderTranslationKey: "ProjectsPage##musicSection##Music Assets",
    buttonLabel: "ProjectsPage##musicSection##addAnotherMusicAssetURL",
    additionalText:
      "ProjectsPage##musicSection##If you don't have a local copy of the asset and it exists on digital platform, you can paste the link below",
    additionalHeader: "ProjectsPage##musicSection##Music asset URL",
    customLinkText: "ProjectsPage##musicSection##Final track URL",
  };

  return (
    <>
      <div css={styles.formItemContainer}>
        <div css={styles.basicInputContainer}>
          <Typography.Text css={styles.basicInputLabel}>
            {t("ProjectsPage##musicSection##Music Assets")}
          </Typography.Text>
        </div>
        <NameLinks
          fileUploader={fileUploader}
          config={config}
          projectOtherAssets={projectOtherAssets}
          updateProjectOtherAssets={updateProjectOtherAssets}
        />
      </div>
      {isMobile && (
        <MobileDrawer
          placement="bottom"
          onClose={toggleDrawer}
          open={isDrawerVisible}
          key="bottom"
          closable={false}
          data-testid="mobile-drawer-container"
          className="documents-drawer-container"
        >
          {generateDrawer(
            activeDrawer,
            handleSetActiveDrawer,
            toggleDrawer,
            accessToken,
            projectId,
            organisationId,
            handleSetMusicAssetsAttachmentsFileList,
            musicAssetsAttachmentsFileList,
            handleShowFileUploadDeleteModal,
            selectedFileToDelete,
            activeFile
          )}
        </MobileDrawer>
      )}
    </>
  );
};

export default MusicAssets;
